import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getUserProfile, getMonthlyActivityMetrics } from '../utils/database';
import { AreaChart, Area, ResponsiveContainer, Tooltip } from 'recharts';
import { useTheme } from '../contexts/ThemeContext';
import DashboardSidebar from '../components/DashboardSidebar';
import BottomNav from '../components/BottomNav';
import '../styles/Dashboard.css';
import '../styles/Performance.css';

function Performance() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [user, setUser] = useState(null);
  const [, setLoading] = useState(true);
  const [monthlyDistanceData, setMonthlyDistanceData] = useState([]);
  const [monthlyDurationData, setMonthlyDurationData] = useState([]);
  const [userSettings, setUserSettings] = useState({ units: 'imperial' });

  // Format distance based on user settings
  const formatDistance = (miles) => {
    if (!miles && miles !== 0) return '0 mi';
    if (userSettings.units === 'metric') {
      const km = miles * 1.60934;
      return km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
    }
    return miles < 10 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`;
  };

  // Format duration in minutes to hours/minutes
  const formatDuration = (minutes) => {
    if (!minutes && minutes !== 0) return '0m';
    if (minutes < 60) return `${Math.round(minutes)}m`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Calculate totals
  const getTotalDistance = () => {
    if (monthlyDistanceData.length === 0) return 0;
    return monthlyDistanceData.reduce((sum, d) => sum + (d.value || 0), 0);
  };

  const getTotalDuration = () => {
    if (monthlyDurationData.length === 0) return 0;
    return monthlyDurationData.reduce((sum, d) => sum + (d.value || 0), 0);
  };

  const getDailyAverageDistance = () => {
    const total = getTotalDistance();
    const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
    return total / daysInMonth;
  };

  const getDailyAverageDuration = () => {
    const total = getTotalDuration();
    const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
    return total / daysInMonth;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        if (!currentUser || currentUser.uid !== userId) {
          navigate('/signin');
          return;
        }

        const userData = await getUserProfile(userId);
        if (!userData) {
          navigate('/onboarding');
          return;
        }

        setUser(userData);
        setUserSettings({
          units: userData.units || 'imperial',
        });

        // Load metrics for last 90 days
        const now = new Date();
        const startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 89); // 90 days including today
        const startDateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
        const endDateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

        const metrics = await getMonthlyActivityMetrics(userId, now.getFullYear(), now.getMonth(), startDateStr, endDateStr);
        if (metrics) {
          setMonthlyDistanceData(metrics.distance || []);
          setMonthlyDurationData(metrics.duration || []);
        }

        setLoading(false);
      } catch (error) {
        console.error('[Performance] Error loading user:', error);
        navigate('/signin');
      }
    });

    return () => unsubscribe();
  }, [userId, navigate]);

  return (
    <>
    <div className="dashboard-page has-bottom-nav">
      {/* Background effects */}
      <div className="dashboard-bg-glow dashboard-bg-glow-1" />
      <div className="dashboard-bg-glow dashboard-bg-glow-2" />
      <div className="dashboard-overlay" />

      {/* Sidebar */}
      <DashboardSidebar userId={userId} user={user} />

      {/* Main content */}
      <div className="dashboard-main">
        {/* Header */}
        <div className="dashboard-header">
          <div className="dashboard-greeting">
            <h1>Performance</h1>
            <p className="dashboard-quote">Track your progress across all metrics</p>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="performance-grid">
          {/* Distance Chart */}
          <div className="performance-card">
            <div className="performance-card-header">
              <h2>Distance</h2>
              <span className="performance-card-subtitle">Last 90 days</span>
            </div>
            <div className="performance-chart-container">
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={monthlyDistanceData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                  <defs>
                    <linearGradient id={isDark ? "perfPurpleGradientDark" : "perfPurpleGradientLight"} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgba(162, 150, 255, 0.40)" />
                      <stop offset="100%" stopColor={isDark ? "rgba(38, 39, 35, 0.10)" : "rgba(255, 255, 255, 0.90)"} />
                    </linearGradient>
                    <filter id={isDark ? "perfPurpleShadowDark" : "perfPurpleShadowLight"} x="-20%" y="-20%" width="140%" height="140%">
                      <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#A296FF" floodOpacity={isDark ? "0.4" : "0.2"}/>
                    </filter>
                  </defs>
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#A296FF" 
                    strokeWidth={2}
                    fill={isDark ? "url(#perfPurpleGradientDark)" : "url(#perfPurpleGradientLight)"}
                    dot={{ fill: isDark ? 'white' : '#1A1A1A', stroke: '#A296FF', strokeWidth: 1, r: 4 }}
                    style={{ filter: isDark ? 'url(#perfPurpleShadowDark)' : 'url(#perfPurpleShadowLight)' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: isDark ? 'rgba(13, 13, 13, 0.95)' : 'rgba(255, 255, 255, 0.95)', 
                      border: `1px solid ${isDark ? 'rgba(162, 150, 255, 0.3)' : 'rgba(162, 150, 255, 0.15)'}`, 
                      borderRadius: '8px',
                      color: isDark ? '#fff' : '#1A1A1A',
                      fontSize: '14px',
                      padding: '10px 14px'
                    }}
                    labelStyle={{ color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)', marginBottom: '4px' }}
                    formatter={(value) => [`${formatDistance(value)}`, 'Distance']}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.date || ''}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="performance-summary-row">
              <div className="performance-summary-item">
                <span className="performance-summary-label">Total</span>
                <span className="performance-summary-value">{formatDistance(getTotalDistance())}</span>
              </div>
              <div className="performance-summary-item">
                <span className="performance-summary-label">Daily Avg</span>
                <span className="performance-summary-value">{formatDistance(getDailyAverageDistance())}</span>
              </div>
            </div>
          </div>

          {/* Duration Chart */}
          <div className="performance-card">
            <div className="performance-card-header">
              <h2>Duration</h2>
              <span className="performance-card-subtitle">Last 90 days</span>
            </div>
            <div className="performance-chart-container">
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={monthlyDurationData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                  <defs>
                    <linearGradient id={isDark ? "perfRedGradientDark" : "perfRedGradientLight"} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgba(255, 0, 0, 0.40)" />
                      <stop offset="100%" stopColor={isDark ? "rgba(38, 39, 35, 0.10)" : "rgba(255, 255, 255, 0.90)"} />
                    </linearGradient>
                    <filter id={isDark ? "perfRedShadowDark" : "perfRedShadowLight"} x="-20%" y="-20%" width="140%" height="140%">
                      <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#FF0000" floodOpacity={isDark ? "0.4" : "0.2"}/>
                    </filter>
                  </defs>
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#FF0000" 
                    strokeWidth={2}
                    fill={isDark ? "url(#perfRedGradientDark)" : "url(#perfRedGradientLight)"}
                    dot={{ fill: isDark ? 'white' : '#1A1A1A', stroke: '#FF0000', strokeWidth: 1, r: 4 }}
                    style={{ filter: isDark ? 'url(#perfRedShadowDark)' : 'url(#perfRedShadowLight)' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: isDark ? 'rgba(13, 13, 13, 0.95)' : 'rgba(255, 255, 255, 0.95)', 
                      border: `1px solid ${isDark ? 'rgba(255, 0, 0, 0.3)' : 'rgba(255, 0, 0, 0.15)'}`, 
                      borderRadius: '8px',
                      color: isDark ? '#fff' : '#1A1A1A',
                      fontSize: '14px',
                      padding: '10px 14px'
                    }}
                    labelStyle={{ color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)', marginBottom: '4px' }}
                    formatter={(value) => [`${formatDuration(value)}`, 'Duration']}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.date || ''}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="performance-summary-row">
              <div className="performance-summary-item">
                <span className="performance-summary-label">Total</span>
                <span className="performance-summary-value">{formatDuration(getTotalDuration())}</span>
              </div>
              <div className="performance-summary-item">
                <span className="performance-summary-label">Daily Avg</span>
                <span className="performance-summary-value">{formatDuration(getDailyAverageDuration())}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Bottom Navigation - Mobile/Tablet */}
    <BottomNav userId={userId} user={user} />
    </>
  );
}

export default Performance;
